/**
 * Van editor-JSON naar HTML die een lezer te zien krijgt.
 *
 * Dit is bewust géén sanitizer die HTML opschoont, maar een **renderer met een witte
 * lijst** (ADR-0037). Het verschil is het hele punt: een sanitizer krijgt HTML binnen en
 * probeert er het gevaarlijke uit te halen — en de geschiedenis van webbeveiliging bestaat
 * uit manieren waarop dat mislukt. Deze functie kan alleen de tags uitschrijven die
 * hieronder staan; er is geen invoer die er een `<script>` uit laat komen, omdat er geen
 * regel is die een `<script>` kan schrijven.
 *
 * De client stuurt dus JSON (het document van TipTap) en nooit HTML, en de server bewaart
 * beide: de JSON om opnieuw te kunnen bewerken, de HTML om te tonen.
 *
 * **Waarom dit een eigen pakket is (Extra 3).** Het voorbeeld in het opstelscherm moet meelopen
 * met wat de schrijver typt, en dus in de browser renderen. Zou daar een tweede renderer staan,
 * dan zou het voorbeeld er na verloop van tijd anders uitzien dan het bericht - en bij een witte
 * lijst is "er anders uitzien" een ander woord voor "iets doorlaten wat de server weghaalt".
 * Eén bestand, twee lezers.
 *
 * Daarom staan er ook geen Node-afhankelijkheden in: dit draait in de browser.
 */

/** De knopen die een bericht mag bevatten. Wat er niet in staat, verdwijnt. */
const BLOCK_NODES = new Set([
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'hardBreak',
  'text',
]);

/** De opmaak die op tekst mag staan. */
const MARKS = new Set(['bold', 'strong', 'italic', 'em', 'underline', 'link']);

/**
 * Koppen beginnen bij `h2`.
 *
 * De titel van het bericht is de `h1` op het scherm; een tweede `h1` in de tekst maakt het
 * document onleesbaar voor een schermlezer, die op koppen navigeert.
 */
const MIN_HEADING = 2;
const MAX_HEADING = 4;

interface Node {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: { type?: string; attrs?: Record<string, unknown> }[];
  content?: Node[];
}

export function renderBodyHtml(document: unknown): string {
  const node = document as Node | null;
  if (!node || typeof node !== 'object') return '';
  return renderChildren(node.content ?? []);
}

function renderChildren(nodes: Node[]): string {
  return nodes.map(renderNode).join('');
}

function renderNode(node: Node): string {
  if (!node || typeof node !== 'object') return '';
  const type = node.type ?? '';
  if (!BLOCK_NODES.has(type)) return '';

  switch (type) {
    case 'text':
      return renderText(node);
    case 'hardBreak':
      return '<br />';
    case 'paragraph': {
      const inner = renderChildren(node.content ?? []);
      // Een lege alinea is in de editor een bewuste witregel; als lege `<p>` verdwijnt hij
      // in de weergave. Een `<br />` erin houdt hem zichtbaar.
      return `<p>${inner || '<br />'}</p>`;
    }
    case 'heading': {
      const level = clampHeading(node.attrs?.level);
      return `<h${level}>${renderChildren(node.content ?? [])}</h${level}>`;
    }
    case 'bulletList':
      return `<ul>${renderChildren(node.content ?? [])}</ul>`;
    case 'orderedList':
      return `<ol>${renderChildren(node.content ?? [])}</ol>`;
    case 'listItem':
      return `<li>${renderChildren(node.content ?? [])}</li>`;
    default:
      return '';
  }
}

function clampHeading(level: unknown): number {
  const value = typeof level === 'number' ? Math.trunc(level) : MIN_HEADING;
  return Math.min(MAX_HEADING, Math.max(MIN_HEADING, value));
}

function renderText(node: Node): string {
  let html = escapeHtml(node.text ?? '');
  if (html === '') return '';

  // Van binnen naar buiten, zodat de nesting klopt: `<a><strong>tekst</strong></a>`.
  for (const mark of node.marks ?? []) {
    const type = mark?.type ?? '';
    if (!MARKS.has(type)) continue;

    switch (type) {
      case 'bold':
      case 'strong':
        html = `<strong>${html}</strong>`;
        break;
      case 'italic':
      case 'em':
        html = `<em>${html}</em>`;
        break;
      case 'underline':
        html = `<u>${html}</u>`;
        break;
      case 'link': {
        const href = safeHref(mark.attrs?.href);
        // Een link met een adres dat we niet vertrouwen (`javascript:`, `data:`) verliest
        // zijn link en houdt zijn tekst. De lezer mist dan een verwijzing; hij krijgt geen
        // knop die iets anders doet dan hij zegt.
        if (!href) break;
        html =
          `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer nofollow">` +
          `${html}</a>`;
        break;
      }
    }
  }

  return html;
}

/**
 * Alleen adressen die in een bericht thuishoren.
 *
 * `javascript:` en `data:` zijn de klassieke manieren om een link in code te veranderen;
 * een relatief adres laten we ook vallen, omdat het in een bericht altijd een vergissing is
 * en in een e-mailweergave (Fase 2) naar een andere plek zou wijzen dan de lezer denkt.
 */
export function safeHref(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol) ? url.toString() : null;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/** Hoeveel tekens er in het kaartje van de feed passen. */
export const EXCERPT_LENGTH = 180;

/**
 * De eerste regels als platte tekst, voor de kaart in de feed.
 *
 * Uit de JSON en niet uit de HTML: tags uit tekst halen is weer een parser, en die hebben we
 * hier net niet nodig. Op een telefoon met een trage verbinding is een feed van twintig
 * volledige berichten het verschil tussen een seconde en tien.
 */
export function excerptOf(document: unknown, length = EXCERPT_LENGTH): string {
  const text = plainText(document as Node | null)
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length <= length) return text;
  const cut = text.slice(0, length);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > length * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * Dezelfde samenvatting, maar uit vertaalde HTML.
 *
 * De gewone weg gaat via de JSON (sneller, en geen parser nodig), maar een vertaling bestaat alleen
 * als HTML: de vertaaldienst krijgt HTML en geeft HTML terug. Voor de feedkaart van een lezer die in
 * een andere taal leest, is dit dus de enige bron. Tags eruit, witruimte samen, en dan hetzelfde
 * afkappen als hierboven.
 */
export function excerptOfHtml(html: string, length = EXCERPT_LENGTH): string {
  const text = html
    // Een blokeinde is een woordgrens, anders plakt "Let op" aan "morgen onderhoud".
    .replace(/<\/(p|li|h[1-6]|div)>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

  if (text.length <= length) return text;
  const cut = text.slice(0, length);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > length * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

function plainText(node: Node | null): string {
  if (!node || typeof node !== 'object') return '';
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'hardBreak') return ' ';
  const inner = (node.content ?? []).map(plainText).join('');
  // Een alinea of een lijstitem eindigt met een spatie, anders plakken twee regels aan
  // elkaar: "Let opmorgen onderhoud".
  return node.type === 'paragraph' || node.type === 'listItem' || node.type === 'heading'
    ? `${inner} `
    : inner;
}
